import { motion } from 'motion/react'
import { useId, useMemo } from 'react'
import styled from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'

import { ValueContext } from '../value-context'

import { UnderlineTabsTab } from './underline-tabs-tab'

import type { ValueContextValue } from '../value-context'
import type {
  ChildrenProps,
  ClassNameProps,
  StyleProps,
} from '@root/shared/react'

type ContextValue = ValueContextValue<{
  indicatorPosition: IndicatorPosition
}>
type IndicatorPosition = 'top' | 'bottom'

type UnderlineTabsProps<T extends string> = {
  accentColor?: string
  textColor?: string
  value: T
  onChange?: (value: T) => void
  indicatorPosition?: IndicatorPosition
} & ChildrenProps &
  ClassNameProps &
  StyleProps

const Tabs_ = <T extends string>({
  value,
  onChange,
  children,
  className,
  indicatorPosition = 'bottom',
  style,
}: UnderlineTabsProps<T>) => {
  const layoutId = useId()
  const haptics = useHapticFeedback()
  const ctx = useMemo(
    (): ContextValue => ({
      value: value,
      onChange: (value) => {
        if (onChange) {
          haptics.impactOccurred('light')
          onChange(value as T)
        }
      },
      layoutId: layoutId,
      indicatorPosition,
    }),
    [haptics, layoutId, onChange, value, indicatorPosition],
  )

  return (
    <Root className={className} style={style}>
      <ValueContext.Provider value={ctx}>{children}</ValueContext.Provider>
    </Root>
  )
}

const Root = styled(motion.div)`
  display: grid;
  grid-auto-columns: auto;
  grid-auto-flow: column;
  position: relative;
  gap: var(--tabs-gap, 0);
  border-bottom: 2px solid var(--backgroundA-3);
`

type Compound = {
  Tab: typeof UnderlineTabsTab
} & typeof Tabs_

const UnderlineTabs = Tabs_ as Compound
UnderlineTabs.Tab = UnderlineTabsTab

export { UnderlineTabs }
